/**
 * useSlashLookup — Detects an active `/` token and drives the portfolio filter:
 *
 *   Tier 1 ("category"): entity types (optionally filtered) + matching values
 *     searched together from the first character after `/`.
 *   Tier 2 ("search"):   user drilled into an entity type; search is scoped
 *     to that type's values (dedicated search box in the dropdown).
 *
 * When `lookupDimensionGroups` is provided, bare `/` shows Time Period plus
 * each dimension group (Product / Market). Drilling a group lists that group's
 * entity types in config order before value search. Unmapped types are hidden.
 *
 * Typing after `/` never auto-drills into a type — siblings stay visible so
 * the user picks explicitly. Exact type matches (e.g. `/brand`) are ranked
 * first but not auto-selected.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { LookupItem, ScoredMatch, searchLookup } from './lookupSearchAlgo';
import type { LookupCategory, LookupDimensionGroup } from '../AskAsperAgentic.types';
import {
    buildCategoriesForDimensionGroup,
    buildConfiguredEntityCategories,
    buildDimensionTopLevelCategories,
    buildLookupCategories,
    filterCategoriesByQuery,
    filterItemsToConfiguredTypes,
    normalizeColumnKey,
    type ScoredCategory,
} from './lookupEntityTypes';

export interface SlashRange {
    /** Inclusive start position of the slash character. */
    start: number;
    /** Exclusive end position (current caret). */
    end: number;
}

/** Replace the in-flight slash query while keeping `/` and trailing text intact. */
export function replaceSlashTokenQuery(
    value: string,
    range: SlashRange | null,
    query: string,
): { nextValue: string; nextCaret: number } | null {
    if (range == null) return null;
    const before = value.slice(0, range.start + 1);
    const after = value.slice(range.end);
    const nextValue = before + query + after;
    return { nextValue, nextCaret: before.length + query.length };
}

export type SlashLookupMode = 'category' | 'search';

/** Flat keyboard-navigable row in tier 1 (entity types first, then values). */
export type SlashFlatRow =
    | { kind: 'category'; scored: ScoredCategory }
    | { kind: 'value'; match: ScoredMatch };

export interface UseSlashLookupResult {
    /** True when an active slash token is detected and the dropdown should render. */
    isActive: boolean;
    /** Which stage we're in. */
    mode: SlashLookupMode;
    /** Text the user has typed between `/` and the caret. */
    query: string;
    /** Replacement range over the input value, or null if no active token. */
    range: SlashRange | null;
    /** Ranked / filtered entity types for tier 1. */
    categories: LookupCategory[];
    /** Scored entity types (includes highlight ranges) for tier 1. */
    scoredCategories: ScoredCategory[];
    /** Matching values across all types (tier 1 only; empty in tier 2). */
    matchingValues: ScoredMatch[];
    /** Flat list used for ↑↓ navigation in tier 1. */
    flatRows: SlashFlatRow[];
    /** True when tier 1 has a query but neither block has matches. */
    hasNoMatches: boolean;
    /** Id of the drilled-in category (null in tier 1). */
    activeCategoryId: string | null;
    /** Label of the drilled-in category (null in tier 1). */
    activeCategoryLabel: string | null;
    /** Id of the active dimension group when drilled past Product/Market (null at top). */
    activeDimensionGroupId: string | null;
    /** Label of the active dimension group (null at top level). */
    activeDimensionGroupLabel: string | null;
    /** Top-N ranked matches scoped to the active category (tier 2). */
    suggestions: ScoredMatch[];
    highlightIndex: number;
    setHighlightIndex: (idx: number) => void;
    pickCategory: (categoryId: string) => void;
    accept: (
        item: LookupItem,
    ) => {
        nextValue: string;
        nextCaret: number;
        item: LookupItem;
        range: { start: number; end: number };
    } | null;
    /**
     * Activate the highlighted row: drills into a category or accepts a value.
     * Returns `'category'` when a type was picked, the accept payload when a
     * value was picked, or null when nothing was actionable.
     */
    activateHighlight: () =>
        | { kind: 'category'; categoryId: string; label: string }
        | {
              kind: 'value';
              nextValue: string;
              nextCaret: number;
              item: LookupItem;
              range: { start: number; end: number };
          }
        | null;
    /** Close the dropdown for the current token (Esc in tier 1). */
    close: () => void;
    /** Return one level (Esc / Back). */
    goBack: () => void;
}

interface SlashTokenInfo {
    slashPos: number;
    query: string;
    range: SlashRange;
}

const WHITESPACE_RE = /\s/;
const DEFAULT_MAX_RESULTS = 30;

/** Re-exported from `lookupEntityTypes` for backward compatibility. */
export { DEFAULT_TIME_PERIOD_ITEMS } from './lookupEntityTypes';

/** Fallback categories when no runtime `lookupItems` are available yet. */
export const DEFAULT_LOOKUP_CATEGORIES = buildLookupCategories([]);

function detectSlashToken(value: string, caretPos: number): SlashTokenInfo | null {
    if (caretPos < 0 || caretPos > value.length) return null;
    for (let i = caretPos - 1; i >= 0; i--) {
        if (value[i] !== '/') continue;
        if (i === 0 || WHITESPACE_RE.test(value[i - 1])) {
            const query = value.slice(i + 1, caretPos);
            return { slashPos: i, query, range: { start: i, end: caretPos } };
        }
    }
    return null;
}

/** Flatten all category items (static + runtime) for cross-type value search. */
function collectAllItems(
    categories: LookupCategory[],
    runtimeItems: LookupItem[] | undefined,
): LookupItem[] {
    const out: LookupItem[] = [];
    const seen = new Set<string>();
    const push = (item: LookupItem) => {
        const key = `${item.column_name}::${item.entity_name}`;
        if (seen.has(key)) return;
        seen.add(key);
        out.push(item);
    };
    for (const cat of categories) {
        if (cat.items) {
            for (const item of cat.items) push(item);
        }
    }
    if (runtimeItems) {
        for (const item of runtimeItems) push(item);
    }
    return out;
}

function findCategoryById(
    categories: LookupCategory[],
    id: string,
): LookupCategory | undefined {
    return categories.find((c) => c.id === id);
}

export interface UseSlashLookupOptions {
    /** Ordered list of categories shown in the picker. Defaults to hierarchy from items. */
    categories?: LookupCategory[];
    /**
     * Host Product / Market buckets. When set (and `categories` is omitted),
     * bare `/` uses dimension top-level rows instead of the flat type list.
     */
    dimensionGroups?: LookupDimensionGroup[];
    /** Maximum number of ranked value suggestions. Default 30. */
    maxResults?: number;
}

export function useSlashLookup(
    value: string,
    caretPos: number,
    items: LookupItem[] | undefined,
    options?: UseSlashLookupOptions,
): UseSlashLookupResult {
    const dimensionGroups = options?.dimensionGroups;
    const hasDimensionGroups =
        Array.isArray(dimensionGroups) && dimensionGroups.length > 0;
    const maxResults = options?.maxResults ?? DEFAULT_MAX_RESULTS;

    const flatFallbackCategories = useMemo(() => {
        if (options?.categories) return options.categories;
        return buildLookupCategories(items ?? []);
    }, [options?.categories, items]);

    const topLevelCategories = useMemo(() => {
        if (options?.categories) return options.categories;
        if (hasDimensionGroups) return buildDimensionTopLevelCategories(dimensionGroups!);
        return flatFallbackCategories;
    }, [options?.categories, hasDimensionGroups, dimensionGroups, flatFallbackCategories]);

    const configuredEntityCategories = useMemo(() => {
        if (!hasDimensionGroups) return [];
        return buildConfiguredEntityCategories(dimensionGroups!, items ?? []);
    }, [hasDimensionGroups, dimensionGroups, items]);

    const token = useMemo(() => detectSlashToken(value, caretPos), [value, caretPos]);

    const [closedSlashPos, setClosedSlashPos] = useState<number | null>(null);
    const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
    const [activeDimensionGroupId, setActiveDimensionGroupId] = useState<string | null>(
        null,
    );
    const [activeForSlashPos, setActiveForSlashPos] = useState<number | null>(null);
    const [highlightIndex, setHighlightIndexState] = useState(0);

    // Reset drill state whenever a *new* slash token starts.
    useEffect(() => {
        if (!token) {
            if (activeCategoryId !== null) setActiveCategoryId(null);
            if (activeDimensionGroupId !== null) setActiveDimensionGroupId(null);
            if (activeForSlashPos !== null) setActiveForSlashPos(null);
            return;
        }
        if (activeForSlashPos !== null && activeForSlashPos !== token.slashPos) {
            setActiveCategoryId(null);
            setActiveDimensionGroupId(null);
            setActiveForSlashPos(null);
        }
    }, [token, activeCategoryId, activeDimensionGroupId, activeForSlashPos]);

    const mode: SlashLookupMode = activeCategoryId == null ? 'category' : 'search';

    const activeDimensionGroup = useMemo(() => {
        if (!hasDimensionGroups || activeDimensionGroupId == null) return null;
        return (
            dimensionGroups!.find((g) => g.id === activeDimensionGroupId) ?? null
        );
    }, [hasDimensionGroups, dimensionGroups, activeDimensionGroupId]);

    const dimensionEntityCategories = useMemo(() => {
        if (!activeDimensionGroup) return [];
        return buildCategoriesForDimensionGroup(activeDimensionGroup, items ?? []);
    }, [activeDimensionGroup, items]);

    /** Categories currently listed in the tier-1 picker (before query filter). */
    const pickerCategories = useMemo(() => {
        if (activeDimensionGroupId != null) return dimensionEntityCategories;
        return topLevelCategories;
    }, [activeDimensionGroupId, dimensionEntityCategories, topLevelCategories]);

    const scoredCategories = useMemo<ScoredCategory[]>(() => {
        if (!token || mode !== 'category') return [];

        const q = token.query.trim();

        // Top-level with dimension groups + typed query: match group labels AND
        // nested entity-type labels (REQ-005 / AC-006).
        if (
            hasDimensionGroups &&
            activeDimensionGroupId == null &&
            q.length > 0 &&
            !options?.categories
        ) {
            const groupMatches = filterCategoriesByQuery(topLevelCategories, q);
            const entityMatches = filterCategoriesByQuery(configuredEntityCategories, q);
            const seen = new Set(groupMatches.map((s) => s.category.id));
            const merged = [...groupMatches];
            for (const scored of entityMatches) {
                if (seen.has(scored.category.id)) continue;
                seen.add(scored.category.id);
                merged.push(scored);
            }
            return merged;
        }

        return filterCategoriesByQuery(pickerCategories, token.query);
    }, [
        token,
        mode,
        hasDimensionGroups,
        activeDimensionGroupId,
        options?.categories,
        topLevelCategories,
        configuredEntityCategories,
        pickerCategories,
    ]);

    const valueSearchItems = useMemo(() => {
        if (hasDimensionGroups && !options?.categories) {
            return filterItemsToConfiguredTypes(items ?? [], dimensionGroups!);
        }
        return items;
    }, [hasDimensionGroups, options?.categories, items, dimensionGroups]);

    const allItems = useMemo(() => {
        if (activeDimensionGroupId != null) {
            return collectAllItems(dimensionEntityCategories, undefined);
        }
        if (hasDimensionGroups && !options?.categories) {
            // Time Period static items + configured runtime items.
            return collectAllItems([topLevelCategories[0]], valueSearchItems);
        }
        return collectAllItems(flatFallbackCategories, items);
    }, [
        activeDimensionGroupId,
        dimensionEntityCategories,
        hasDimensionGroups,
        options?.categories,
        topLevelCategories,
        valueSearchItems,
        flatFallbackCategories,
        items,
    ]);

    const matchingValues = useMemo<ScoredMatch[]>(() => {
        if (!token || mode !== 'category') return [];
        // Values block only appears once the user starts typing after `/`.
        if (token.query.trim().length === 0) return [];
        return searchLookup(token.query, allItems, maxResults);
    }, [token, mode, allItems, maxResults]);

    const flatRows = useMemo<SlashFlatRow[]>(() => {
        if (mode !== 'category') return [];
        return [
            ...scoredCategories.map((scored) => ({ kind: 'category' as const, scored })),
            ...matchingValues.map((match) => ({ kind: 'value' as const, match })),
        ];
    }, [mode, scoredCategories, matchingValues]);

    const searchableCategories = useMemo(() => {
        if (hasDimensionGroups && !options?.categories) {
            return [
                ...topLevelCategories,
                ...configuredEntityCategories,
                ...dimensionEntityCategories,
            ];
        }
        return flatFallbackCategories;
    }, [
        hasDimensionGroups,
        options?.categories,
        topLevelCategories,
        configuredEntityCategories,
        dimensionEntityCategories,
        flatFallbackCategories,
    ]);

    const activeCategory = useMemo(() => {
        if (activeCategoryId == null) return null;
        return (
            findCategoryById(searchableCategories, activeCategoryId) ??
            findCategoryById(pickerCategories, activeCategoryId) ??
            null
        );
    }, [activeCategoryId, searchableCategories, pickerCategories]);

    const categoryItems = useMemo<LookupItem[]>(() => {
        if (!activeCategory) return [];
        if (activeCategory.items && activeCategory.items.length > 0) {
            return activeCategory.items;
        }
        // Runtime items filtered by column when category has no static items.
        const key = normalizeColumnKey(activeCategory.id);
        const pool = valueSearchItems ?? items ?? [];
        return pool.filter((item) => normalizeColumnKey(item.column_name) === key);
    }, [activeCategory, valueSearchItems, items]);

    const suggestions = useMemo<ScoredMatch[]>(() => {
        if (!token || mode !== 'search') return [];
        if (categoryItems.length === 0) return [];
        return searchLookup(token.query, categoryItems, maxResults);
    }, [token, mode, categoryItems, maxResults]);

    const hasNoMatches =
        mode === 'category' &&
        token != null &&
        token.query.trim().length > 0 &&
        scoredCategories.length === 0 &&
        matchingValues.length === 0;

    // Reset highlight whenever the visible list materially changes.
    useEffect(() => {
        setHighlightIndexState(0);
    }, [token?.slashPos, token?.query, activeCategoryId, activeDimensionGroupId]);

    // Clear closedSlashPos when token disappears or the slash moves.
    useEffect(() => {
        if (closedSlashPos !== null && (!token || token.slashPos !== closedSlashPos)) {
            setClosedSlashPos(null);
        }
    }, [token, closedSlashPos]);

    const isActive =
        token !== null &&
        closedSlashPos !== token.slashPos &&
        (mode === 'category'
            ? pickerCategories.length > 0 || scoredCategories.length > 0
            : activeCategoryId != null);

    const close = useCallback(() => {
        if (token) setClosedSlashPos(token.slashPos);
    }, [token]);

    const goBack = useCallback(() => {
        if (activeCategoryId != null) {
            setActiveCategoryId(null);
            // Stay inside the dimension group when backing out of value search.
            if (activeDimensionGroupId != null) {
                setActiveForSlashPos(token?.slashPos ?? activeForSlashPos);
                return;
            }
            setActiveForSlashPos(null);
            return;
        }
        if (activeDimensionGroupId != null) {
            setActiveDimensionGroupId(null);
            setActiveForSlashPos(null);
            return;
        }
        setActiveForSlashPos(null);
    }, [
        activeCategoryId,
        activeDimensionGroupId,
        token?.slashPos,
        activeForSlashPos,
    ]);

    const accept = useCallback(
        (item: LookupItem) => {
            if (!token) return null;
            const before = value.slice(0, token.range.start);
            const after = value.slice(token.range.end);
            const insertion = item.entity_name;
            const nextValue = before + insertion + after;
            const rangeStart = before.length;
            const rangeEnd = rangeStart + insertion.length;
            const nextCaret = rangeEnd;
            setClosedSlashPos(token.slashPos);
            return {
                nextValue,
                nextCaret,
                item,
                range: { start: rangeStart, end: rangeEnd },
            };
        },
        [token, value],
    );

    const pickCategory = useCallback(
        (id: string) => {
            if (!token) return;

            // Prefer the currently visible / searchable rows (includes nested
            // entity types surfaced during top-level typed search).
            const fromScored = scoredCategories.find((s) => s.category.id === id)?.category;
            const category =
                fromScored ??
                findCategoryById(pickerCategories, id) ??
                findCategoryById(searchableCategories, id);
            if (!category) return;

            setActiveForSlashPos(token.slashPos);

            if (category.dimensionGroupId) {
                setActiveDimensionGroupId(category.dimensionGroupId);
                setActiveCategoryId(null);
                return;
            }

            setActiveCategoryId(category.id);
        },
        [token, scoredCategories, pickerCategories, searchableCategories],
    );

    const visibleListLength =
        mode === 'category'
            ? hasNoMatches
                ? 0
                : flatRows.length
            : suggestions.length;

    const setHighlightIndex = useCallback(
        (idx: number) => {
            if (visibleListLength === 0) {
                setHighlightIndexState(0);
                return;
            }
            const n = visibleListLength;
            const wrapped = ((idx % n) + n) % n;
            setHighlightIndexState(wrapped);
        },
        [visibleListLength],
    );

    const clampedHighlight =
        visibleListLength === 0 ? 0 : Math.min(highlightIndex, visibleListLength - 1);

    const activateHighlight = useCallback(() => {
        if (mode === 'category') {
            const row = flatRows[clampedHighlight];
            if (!row) return null;
            if (row.kind === 'category') {
                pickCategory(row.scored.category.id);
                return {
                    kind: 'category' as const,
                    categoryId: row.scored.category.id,
                    label: row.scored.category.label,
                };
            }
            const accepted = accept(row.match.item);
            if (!accepted) return null;
            return { kind: 'value' as const, ...accepted };
        }
        const match = suggestions[clampedHighlight];
        if (!match) return null;
        const accepted = accept(match.item);
        if (!accepted) return null;
        return { kind: 'value' as const, ...accepted };
    }, [mode, flatRows, clampedHighlight, pickCategory, accept, suggestions]);

    return {
        isActive,
        mode,
        query: token?.query ?? '',
        range: token?.range ?? null,
        categories: scoredCategories.map((s) => s.category),
        scoredCategories,
        matchingValues,
        flatRows,
        hasNoMatches,
        activeCategoryId,
        activeCategoryLabel: activeCategory?.label ?? null,
        activeDimensionGroupId,
        activeDimensionGroupLabel: activeDimensionGroup?.label ?? null,
        suggestions,
        highlightIndex: clampedHighlight,
        setHighlightIndex,
        pickCategory,
        accept,
        activateHighlight,
        close,
        goBack,
    };
}
